import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';

import cx from '@3ui/cx';

import { Tab } from './Tab';
import styles from './Tabs.module.css';
import { TabsContainerProps as Props, TabsLine } from './types';

const getSelectedTabIndex = (tabs, selected): number => {
  let idx;
  if (selected) {
    idx = tabs.findIndex((item) =>
      typeof item === 'string' ? item === selected : item.title === selected
    );
  } else {
    idx = tabs.findIndex((item) => item.isSelected);
  }

  return idx > 0 ? idx : 0;
};

const getTabProps = (tabProps) =>
  typeof tabProps === 'string' ? { title: tabProps } : tabProps;

const getLineStyles = (tab, container): TabsLine | null => {
  if (!tab || !container) {
    return null;
  }
  const { left, width } = tab.getBoundingClientRect();
  return {
    width,
    left: left - container.getBoundingClientRect().left
  };
};

export const Tabs = ({
  tabs,
  selected,
  style = {},
  className,
  el,
  onSelect
}: Props) => {
  const selectedTabRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState({
    selectedTabIndex: getSelectedTabIndex(tabs, selected)
  });
  const [lineStyle, setLineStyle] = useState<TabsLine | null>(null);
  const { selectedTabIndex } = state;

  useEffect(() => {
    setState((prevProps) => ({
      ...prevProps,
      selectedTabIndex: getSelectedTabIndex(tabs, selected)
    }));
  }, [tabs, selected]);

  useLayoutEffect(() => {
    setLineStyle(() =>
      getLineStyles(selectedTabRef?.current, containerRef?.current)
    );
  }, [selectedTabRef, containerRef, selectedTabIndex]);

  const onTabClick = (tabIndex: number, onClick) => (event) => {
    setState((prevProps) => ({
      ...prevProps,
      selectedTabIndex: tabIndex
    }));
    onClick?.(event);
    onSelect?.(event, tabs[tabIndex]);
  };

  return (
    <div
      className={cx(styles.root, className)}
      style={style}
      ref={containerRef}
    >
      {tabs.map((tab, tabIndex) => {
        const tabProps = getTabProps(tab);
        const isSelected = selectedTabIndex === tabIndex;
        return (
          <Tab
            key={tabIndex}
            el={el}
            ref={isSelected ? selectedTabRef : null}
            {...tabProps}
            isSelected={isSelected}
            onClick={onTabClick(tabIndex, tabProps.onClick)}
          >
            {tabProps.title}
          </Tab>
        );
      })}
      {lineStyle && <div className={styles.line} style={lineStyle} />}
    </div>
  );
};
